<template>
    <TailwindLayout :title="`Support · ${supportUser.name}`">
        <div class="space-y-6">
            <!-- Flash -->
            <div v-if="$page.props.flash && $page.props.flash.success" class="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
                {{ $page.props.flash.success }}
            </div>
            <div v-if="$page.props.flash && $page.props.flash.error" class="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
                {{ $page.props.flash.error }}
            </div>

            <!-- Back link -->
            <BridgeBackLink />

            <!-- Header / identity -->
            <div class="relative overflow-hidden rounded-2xl border border-rent-risk-blue-100 bg-gradient-to-br from-rent-risk-blue-100 via-white to-white px-6 py-6">
                <div class="flex flex-wrap items-start justify-between gap-4">
                    <div class="flex items-start gap-4">
                        <span class="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-rent-risk-blue-400 text-lg font-semibold text-white shadow-sm">
                            {{ initials }}
                        </span>
                        <div>
                            <div class="flex flex-wrap items-center gap-2">
                                <h1 class="font-bold text-2xl leading-tight text-rent-risk-blue-400">{{ supportUser.name || 'Unnamed user' }}</h1>
                                <span class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize" :class="roleBadgeClass(supportUser.role)">{{ supportUser.role }}</span>
                                <span v-if="supportUser.deleted_at" class="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">deleted {{ supportUser.deleted_at }}</span>
                                <span
                                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                    :class="supportUser.is_experian_verified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'"
                                >
                                    {{ supportUser.is_experian_verified ? 'Experian verified' : 'Not Experian verified' }}
                                </span>
                            </div>
                            <dl class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-sm">
                                <div class="flex gap-2"><dt class="text-gray-500">Email</dt><dd class="text-gray-900">{{ supportUser.email }}</dd></div>
                                <div class="flex gap-2"><dt class="text-gray-500">User ID</dt><dd class="font-mono text-gray-700">#{{ supportUser.id }}</dd></div>
                                <div class="flex gap-2"><dt class="text-gray-500">Cell</dt><dd class="text-gray-900">{{ supportUser.cell_phone || '—' }}</dd></div>
                                <div class="flex gap-2"><dt class="text-gray-500">Home</dt><dd class="text-gray-900">{{ supportUser.home_phone || '—' }}</dd></div>
                                <div class="flex gap-2"><dt class="text-gray-500">Registered</dt><dd class="text-gray-900">{{ supportUser.created_at || '—' }}</dd></div>
                            </dl>
                        </div>
                    </div>
                    <button
                        type="button"
                        class="inline-flex shrink-0 items-center gap-2 rounded-lg bg-rent-risk-blue-400 px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
                        @click="impersonate"
                    >
                        <font-awesome-icon :icon="faRightToBracket" class="h-4 w-4" /> Impersonate user
                    </button>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <!-- Subscription (agents): price/status live from DB, lifetime value from cached Stripe snapshot -->
                <Card v-if="supportUser.role === 'Agent'" title="Subscription" class="lg:col-span-2">
                    <Deferred data="subscription">
                        <template #fallback><SkeletonLines :rows="3" /></template>
                        <p v-if="loadFailed(subscription)" class="text-sm text-red-600">Couldn't load this panel. Refresh the page to try again.</p>
                        <template v-else>
                        <div v-if="!subView.has_subscription" class="text-sm text-gray-500">No active subscription on record.</div>
                        <div v-else class="space-y-3 text-sm">
                            <div class="flex flex-wrap items-center gap-2">
                                <span class="font-semibold text-gray-900">{{ subView.plan }}</span>
                                <span class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize" :class="subStatusBadge(subView.status)">{{ subView.status || 'unknown' }}</span>
                            </div>
                            <dl class="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-1 text-gray-600">
                                <div class="flex gap-2"><dt class="text-gray-500">Price</dt><dd class="text-gray-900">{{ money(subView.price) }}<span v-if="subView.interval"> / {{ subView.interval === 'annual' ? 'yr' : 'mo' }}</span></dd></div>
                                <div class="flex gap-2"><dt class="text-gray-500">Paid invoices</dt><dd class="text-gray-900">{{ subView.paid_invoices ?? '—' }}</dd></div>
                                <div class="flex gap-2"><dt class="text-gray-500">Lifetime value</dt><dd class="font-semibold text-gray-900">{{ subView.lifetime_value === null || subView.lifetime_value === undefined ? 'unavailable' : money(subView.lifetime_value) }}</dd></div>
                            </dl>
                            <p v-if="subView.synced_ago" class="text-xs text-gray-400">Lifetime value synced {{ subView.synced_ago }}</p>
                        </div>
                        </template>
                    </Deferred>
                    <template #action>
                        <button type="button" class="rounded-lg border border-rent-risk-blue-400 px-3 py-1.5 text-xs font-semibold text-rent-risk-blue-400 transition hover:bg-rent-risk-blue-400 hover:text-white disabled:opacity-50" :disabled="syncing" @click="syncStripe">
                            {{ syncing ? 'Syncing…' : 'Sync from Stripe' }}
                        </button>
                    </template>
                </Card>

                <!-- Payments (applicants): total paid live from DB, refunds from cached Stripe snapshot -->
                <Card v-if="supportUser.role === 'Applicant'" title="Payments" class="lg:col-span-2">
                    <Deferred data="payments">
                        <template #fallback><SkeletonLines :rows="3" /></template>
                        <p v-if="loadFailed(payments)" class="text-sm text-red-600">Couldn't load this panel. Refresh the page to try again.</p>
                        <template v-else>
                        <dl class="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-1 text-sm text-gray-600">
                            <div class="flex gap-2"><dt class="text-gray-500">Total paid</dt><dd class="text-gray-900">{{ money(payView.total_paid) }}</dd></div>
                            <div class="flex gap-2"><dt class="text-gray-500">Refunded</dt><dd class="text-gray-900">{{ payView.refunded_available ? money(payView.total_refunded) : 'unavailable' }}</dd></div>
                            <div class="flex gap-2"><dt class="text-gray-500">Lifetime value</dt><dd class="font-semibold text-gray-900">{{ money(payView.lifetime_value) }}</dd></div>
                        </dl>
                        <p v-if="payView.synced_ago" class="mt-2 text-xs text-gray-400">Refunds synced {{ payView.synced_ago }}</p>
                        </template>
                    </Deferred>
                    <template #action>
                        <button type="button" class="rounded-lg border border-rent-risk-blue-400 px-3 py-1.5 text-xs font-semibold text-rent-risk-blue-400 transition hover:bg-rent-risk-blue-400 hover:text-white disabled:opacity-50" :disabled="syncing" @click="syncStripe">
                            {{ syncing ? 'Syncing…' : 'Sync from Stripe' }}
                        </button>
                    </template>
                </Card>

                <!-- Experian attempts + reset action -->
                <Card title="Experian Verification Attempts">
                    <Deferred data="experianAttempts">
                        <template #fallback><SkeletonLines :rows="2" /></template>
                        <p v-if="loadFailed(experianAttempts)" class="text-sm text-red-600">Couldn't load this panel. Refresh the page to try again.</p>
                        <template v-else>
                        <div>
                            <div v-if="!experianAttempts.has_record" class="text-sm text-gray-500">No verification attempts on record yet.</div>
                            <div v-else class="space-y-3">
                                <div class="flex items-center gap-3">
                                    <span
                                        class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                        :class="(experianAttempts.is_locked || experianAttempts.attempts_remaining === 0) ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'"
                                    >
                                        {{ experianAttempts.is_locked ? 'Locked' : (experianAttempts.attempts_remaining === 0 ? 'No attempts left' : 'Active') }}
                                    </span>
                                    <span class="text-sm text-gray-700">
                                        <span class="font-semibold">{{ experianAttempts.attempts_remaining }}</span> attempt{{ experianAttempts.attempts_remaining === 1 ? '' : 's' }} remaining
                                    </span>
                                </div>
                                <p v-if="experianAttempts.is_locked" class="text-sm text-gray-600">
                                    Locked until {{ experianAttempts.locked_until }} ({{ experianAttempts.remaining_lockout_time }} left).
                                </p>
                            </div>
                        </div>
                        </template>
                    </Deferred>
                    <template #action>
                        <button
                            v-if="experianAttempts.has_record"
                            type="button"
                            class="rounded-lg bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                            :disabled="resetting"
                            @click="resetExperian"
                        >
                            {{ resetting ? 'Resetting…' : 'Reset attempts to 3 / unlock' }}
                        </button>
                    </template>
                </Card>

                <!-- KYC / IDV -->
                <Card title="Identity Verification (Plaid KYC)">
                    <Deferred data="kyc">
                        <template #fallback><SkeletonLines :rows="2" /></template>
                        <p v-if="loadFailed(kyc)" class="text-sm text-red-600">Couldn't load this panel. Refresh the page to try again.</p>
                        <template v-else>
                        <div v-if="!kyc.has_record" class="text-sm text-gray-500">No KYC record.</div>
                        <div v-else class="space-y-2">
                            <span class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium" :class="kycBadgeClass(kyc.status)">
                                {{ kyc.status || 'unknown' }}
                            </span>
                            <dl class="text-sm text-gray-600 space-y-1">
                                <div class="flex gap-2"><dt class="text-gray-500">IDV ID</dt><dd class="font-mono">{{ kyc.idv_id || '—' }}</dd></div>
                                <div class="flex gap-2"><dt class="text-gray-500">Updated</dt><dd>{{ kyc.updated_at || '—' }}</dd></div>
                            </dl>
                        </div>
                        </template>
                    </Deferred>
                </Card>

                <!-- Bank account -->
                <Card title="Rent Payment Bank Account">
                    <Deferred data="bankAccount">
                        <template #fallback><SkeletonLines :rows="2" /></template>
                        <p v-if="loadFailed(bankAccount)" class="text-sm text-red-600">Couldn't load this panel. Refresh the page to try again.</p>
                        <template v-else>
                        <div v-if="!bankAccount.connected" class="text-sm text-gray-500">No bank account connected.</div>
                        <div v-else class="space-y-1 text-sm">
                            <span class="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">Connected</span>
                            <dl class="mt-2 text-gray-600 space-y-1">
                                <div class="flex gap-2"><dt class="text-gray-500">Bank</dt><dd class="text-gray-900">{{ bankAccount.bank_name || '—' }}</dd></div>
                                <div class="flex gap-2"><dt class="text-gray-500">Account name</dt><dd class="text-gray-900">{{ bankAccount.account_name || '—' }}</dd></div>
                                <div class="flex gap-2"><dt class="text-gray-500">Last 4</dt><dd class="font-mono text-gray-900">•••• {{ bankAccount.last_4 || '????' }}</dd></div>
                            </dl>
                        </div>
                        </template>
                    </Deferred>
                </Card>

                <!-- Last login -->
                <Card title="Last Login">
                    <Deferred data="lastLogin">
                        <template #fallback><SkeletonLines :rows="2" /></template>
                        <p v-if="loadFailed(lastLogin)" class="text-sm text-red-600">Couldn't load this panel. Refresh the page to try again.</p>
                        <template v-else>
                        <div v-if="!lastLogin.has_record" class="text-sm text-gray-500">No successful login recorded.</div>
                        <dl v-else class="text-sm text-gray-600 space-y-1">
                            <div class="flex gap-2"><dt class="text-gray-500">When</dt><dd class="text-gray-900">{{ lastLogin.last_login_at }}</dd></div>
                            <div class="flex gap-2"><dt class="text-gray-500">IP</dt><dd class="font-mono">{{ lastLogin.ip_address || '—' }}</dd></div>
                        </dl>
                        </template>
                    </Deferred>
                </Card>

                <!-- Last 3 failed Experian responses -->
                <Card title="Last 3 Failed Experian Responses">
                    <Deferred data="failedExperianResponses">
                        <template #fallback><SkeletonLines :rows="3" /></template>
                        <p v-if="loadFailed(failedExperianResponses)" class="text-sm text-red-600">Couldn't load this panel. Refresh the page to try again.</p>
                        <template v-else>
                        <div v-if="failedExperianResponses.length === 0" class="text-sm text-gray-500">No failed Experian responses.</div>
                        <table v-else class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error code</th>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">When</th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                                <tr v-for="resp in failedExperianResponses" :key="resp.id">
                                    <td class="px-3 py-2 text-sm font-mono text-gray-700">{{ resp.error_code || '—' }}</td>
                                    <td class="px-3 py-2 text-sm text-gray-700">{{ resp.error_message || '—' }}</td>
                                    <td class="px-3 py-2 text-sm text-gray-500">{{ resp.created_at }}</td>
                                </tr>
                            </tbody>
                        </table>
                        </template>
                    </Deferred>
                </Card>

                <!-- Experian authentication: stored state now, on-demand live check -->
                <Card title="Experian Authentication">
                    <Deferred data="experianAuth">
                        <template #fallback><SkeletonLines :rows="3" /></template>
                        <p v-if="loadFailed(experianAuth)" class="text-sm text-red-600">Couldn't load this panel. Refresh the page to try again.</p>
                        <template v-else>
                        <div class="space-y-3 text-sm">
                            <div class="flex flex-wrap items-center gap-2">
                                <span
                                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                    :class="experianAuth.is_experian_verified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'"
                                >
                                    {{ experianAuth.is_experian_verified ? 'Verified locally' : 'Not verified locally' }}
                                </span>
                            </div>
                            <dl class="grid grid-cols-1 gap-y-1 text-gray-600">
                                <div class="flex gap-2"><dt class="text-gray-500">User token</dt><dd :class="experianAuth.user_token_present ? 'text-gray-900' : 'text-gray-400'">{{ experianAuth.user_token_present ? 'present' : 'missing' }}</dd></div>
                                <div class="flex gap-2"><dt class="text-gray-500">Auth session</dt><dd :class="experianAuth.auth_session_present ? 'text-gray-900' : 'text-gray-400'">{{ experianAuth.auth_session_present ? 'present' : 'missing' }}</dd></div>
                                <div class="flex gap-2">
                                    <dt class="text-gray-500">Token expiry</dt>
                                    <dd :class="expiryStateClass(experianAuth.expiry_state)">
                                        {{ experianAuth.expiry_date || '—' }}<span v-if="experianAuth.expiry_state && experianAuth.expiry_state !== 'blank'"> ({{ experianAuth.expiry_state }})</span>
                                    </dd>
                                </div>
                            </dl>

                            <div
                                v-if="experianLive"
                                class="rounded-md border px-3 py-2"
                                :class="liveCardClass"
                            >
                                <div class="flex flex-wrap items-center gap-2">
                                    <span
                                        class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                        :class="livePillClass"
                                    >
                                        {{ livePillText }}
                                    </span>
                                    <span v-if="experianLive.expiration" class="text-xs text-gray-500">expires {{ experianLive.expiration }}</span>
                                </div>
                                <p class="mt-2 text-xs" :class="liveInterpretationClass">{{ liveInterpretation }}</p>
                                <p v-if="experianLiveErrorText" class="mt-1 text-xs text-red-600">{{ experianLiveErrorText }}</p>
                            </div>
                            <p v-if="experianLiveError" class="text-xs text-red-600">{{ experianLiveError }}</p>
                        </div>
                        </template>
                    </Deferred>
                    <template #action>
                        <button
                            type="button"
                            class="rounded-lg border border-rent-risk-blue-400 px-3 py-1.5 text-xs font-semibold text-rent-risk-blue-400 transition hover:bg-rent-risk-blue-400 hover:text-white disabled:opacity-50"
                            :disabled="experianLiveLoading"
                            @click="checkExperianLive"
                        >
                            {{ experianLiveLoading ? 'Checking…' : 'Check live Experian status' }}
                        </button>
                    </template>
                </Card>

                <!-- Applications -->
                <Card title="Applications" class="lg:col-span-2">
                    <Deferred data="applications">
                        <template #fallback><SkeletonLines :rows="3" /></template>
                        <p v-if="loadFailed(applications)" class="text-sm text-red-600">Couldn't load this panel. Refresh the page to try again.</p>
                        <template v-else>
                        <div v-if="applications.length === 0" class="text-sm text-gray-500">No applications.</div>
                        <table v-else class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property</th>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next step</th>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                                <tr v-for="application in applications" :key="application.id">
                                    <td class="px-3 py-2 text-sm">
                                        <div class="text-gray-900">{{ application.property_address || '—' }}</div>
                                        <div v-if="supportUser.role === 'Agent'" class="text-xs text-gray-500">
                                            {{ application.applicant_name || 'Unknown applicant' }}<span v-if="application.applicant_email"> · {{ application.applicant_email }}</span>
                                        </div>
                                        <div v-else class="text-xs text-gray-500">
                                            {{ application.landlord_name || 'Unknown landlord' }}<span v-if="application.landlord_email"> · {{ application.landlord_email }}</span>
                                        </div>
                                    </td>
                                    <td class="px-3 py-2 text-sm">
                                        <span
                                            class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize"
                                            :class="applicationStatusBadge(application.status)"
                                        >
                                            {{ humanizeStatus(application.status) }}
                                        </span>
                                    </td>
                                    <td class="px-3 py-2 text-sm">
                                        <span
                                            class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                                            :class="application.is_complete ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'"
                                        >
                                            {{ application.completion_percentage }}%
                                        </span>
                                    </td>
                                    <td class="px-3 py-2 text-sm text-gray-700">{{ application.next_step || (application.is_complete ? 'Complete' : '—') }}</td>
                                    <td class="px-3 py-2 text-sm text-gray-500">{{ application.created_at }}</td>
                                </tr>
                            </tbody>
                        </table>
                        </template>
                    </Deferred>
                </Card>

                <!-- Last 5 emails -->
                <Card title="Last 5 Emails Sent" class="lg:col-span-2">
                    <Deferred data="emails">
                        <template #fallback><SkeletonLines :rows="3" /></template>
                        <p v-if="loadFailed(emails)" class="text-sm text-red-600">Couldn't load this panel. Refresh the page to try again.</p>
                        <template v-else>
                        <div v-if="emails.length === 0" class="text-sm text-gray-500">No emails sent to this user.</div>
                        <table v-else class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                                <tr v-for="email in emails" :key="email.id">
                                    <td class="px-3 py-2 text-sm">
                                        <a :href="route('email-logs.show', email.id)" target="_blank" rel="noopener" class="text-rent-risk-blue-400 hover:underline">
                                            {{ email.subject || '(no subject)' }}
                                        </a>
                                    </td>
                                    <td class="px-3 py-2 text-sm text-gray-500 font-mono">{{ email.mail_class || '—' }}</td>
                                    <td class="px-3 py-2 text-sm">
                                        <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium" :class="emailBadgeClass(email.status)">
                                            {{ email.status }}
                                        </span>
                                    </td>
                                    <td class="px-3 py-2 text-sm text-gray-500">{{ email.sent_at || email.created_at }}</td>
                                </tr>
                            </tbody>
                        </table>
                        </template>
                    </Deferred>
                </Card>
            </div>
        </div>
    </TailwindLayout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue';
import Card from './Card.vue';
import SkeletonLines from './SkeletonLines.vue';
import { Deferred, Link, router } from '@inertiajs/vue3';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faRightToBracket } from '@fortawesome/pro-light-svg-icons';
import BridgeBackLink from '@/Shared/BridgeBackLink.vue';
import axios from 'axios';

export default {
    components: { TailwindLayout, Card, SkeletonLines, Deferred, Link, FontAwesomeIcon, BridgeBackLink },
    props: {
        supportUser: { type: Object, required: true },
        subscription: { type: Object, default: () => ({}) },
        payments: { type: Object, default: () => ({}) },
        applications: { type: [Array, Object], default: () => [] },
        experianAttempts: { type: Object, default: () => ({}) },
        experianAuth: { type: Object, default: () => ({}) },
        failedExperianResponses: { type: [Array, Object], default: () => [] },
        emails: { type: [Array, Object], default: () => [] },
        kyc: { type: Object, default: () => ({}) },
        bankAccount: { type: Object, default: () => ({}) },
        lastLogin: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            resetting: false,
            syncing: false,
            experianLive: null,
            experianLiveLoading: false,
            experianLiveError: null,
            faRightToBracket,
            // Async sync overrides for the Stripe-derived tile figures, so a
            // sync updates the tile in place without an Inertia page visit.
            subscriptionData: null,
            paymentsData: null,
        };
    },
    computed: {
        subView() {
            return this.subscriptionData || this.subscription;
        },
        payView() {
            return this.paymentsData || this.payments;
        },
        initials() {
            const name = (this.supportUser.name || '').trim();
            if (!name) {
                return '—';
            }
            return name.split(/\s+/).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('');
        },
        liveUnreachable() {
            return !!this.experianLive && this.experianLive.reachable === false;
        },
        livePillText() {
            if (this.liveUnreachable) {
                return 'Experian: status unavailable';
            }
            return this.experianLive && this.experianLive.authenticated
                ? 'Experian: authenticated'
                : 'Experian: NOT authenticated';
        },
        livePillClass() {
            if (this.liveUnreachable) {
                return 'bg-gray-100 text-gray-700';
            }
            return this.experianLive && this.experianLive.authenticated
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700';
        },
        liveCardClass() {
            if (this.liveUnreachable) {
                return 'border-gray-200 bg-gray-50';
            }
            return this.experianLive && this.experianLive.authenticated
                ? 'border-green-200 bg-green-50'
                : 'border-amber-200 bg-amber-50';
        },
        liveInterpretation() {
            if (!this.experianLive) {
                return '';
            }
            if (this.liveUnreachable) {
                return 'Couldn\'t reach Experian for a live check — this is a transient error, not a deauthentication. It doesn\'t change the user\'s stored status; try again in a moment.';
            }
            const storedVerified = !!this.experianAuth.is_experian_verified;
            const liveAuth = !!this.experianLive.authenticated;
            if (!storedVerified) {
                return 'Not verified locally — RentRisk has no valid stored Experian token, so this user is already treated as unverified.';
            }
            if (liveAuth) {
                return 'Healthy — stored state and Experian agree this user is authenticated.';
            }
            return 'Token drift: Experian has deauthed this user; they\'ll be forced to re-verify on report view. Remediate with experian:reconcile-agent-auth / experian:reverify-deauthed-agents.';
        },
        liveInterpretationClass() {
            if (this.liveUnreachable) {
                return 'text-gray-600';
            }
            const storedVerified = !!this.experianAuth.is_experian_verified;
            const liveAuth = !!(this.experianLive && this.experianLive.authenticated);
            if (storedVerified && liveAuth) {
                return 'text-green-700';
            }
            if (storedVerified && !liveAuth) {
                return 'text-red-600';
            }
            return 'text-gray-600';
        },
        experianLiveErrorText() {
            const e = this.experianLive && this.experianLive.error;
            if (!e) {
                return '';
            }
            if (typeof e === 'string') {
                return e;
            }
            return e.message || e.Message || JSON.stringify(e);
        },
    },
    methods: {
        // The server returns { load_failed: true } for a panel whose query
        // errored or ran past its time cap.
        loadFailed(panel) {
            return Boolean(panel && !Array.isArray(panel) && panel.load_failed);
        },
        resetExperian() {
            if (!window.confirm(`Reset Experian attempts to 3 and unlock ${this.supportUser.name}?`)) {
                return;
            }
            router.post(route('bridge.support.reset-experian', this.supportUser.id), {}, {
                preserveScroll: true,
                onStart: () => { this.resetting = true; },
                onFinish: () => { this.resetting = false; },
            });
        },
        impersonate() {
            if (!window.confirm(`Impersonate ${this.supportUser.name}? You will be logged in as this user.`)) {
                return;
            }
            router.post(route('impersonate', this.supportUser.id));
        },
        checkExperianLive() {
            this.experianLiveLoading = true;
            this.experianLiveError = null;
            axios.get(route('bridge.support.experian-live-check', this.supportUser.id))
                .then((response) => {
                    this.experianLive = response.data;
                })
                .catch(() => {
                    this.experianLiveError = 'Could not reach Experian. Please try again.';
                })
                .finally(() => {
                    this.experianLiveLoading = false;
                });
        },
        expiryStateClass(state) {
            if (state === 'future') {
                return 'text-gray-900';
            }
            if (state === 'past') {
                return 'text-red-600';
            }
            return 'text-gray-400';
        },
        kycBadgeClass(status) {
            if (status === 'success') return 'bg-green-100 text-green-700';
            if (status === 'failed') return 'bg-red-100 text-red-700';
            return 'bg-amber-100 text-amber-700';
        },
        emailBadgeClass(status) {
            if (status === 'sent') return 'bg-green-100 text-green-700';
            if (status === 'failed' || status === 'bounced') return 'bg-red-100 text-red-700';
            return 'bg-gray-100 text-gray-700';
        },
        roleBadgeClass(role) {
            const r = (role || '').toLowerCase();
            if (r === 'agent') return 'bg-violet-100 text-violet-700';
            if (r === 'applicant') return 'bg-amber-100 text-amber-700';
            return 'bg-gray-100 text-gray-700';
        },
        subStatusBadge(status) {
            const s = (status || '').toLowerCase();
            if (['active', 'trialing'].includes(s)) return 'bg-green-100 text-green-700';
            if (['past_due', 'incomplete', 'unpaid'].includes(s)) return 'bg-amber-100 text-amber-700';
            if (['canceled', 'cancelled', 'incomplete_expired'].includes(s)) return 'bg-red-100 text-red-700';
            return 'bg-gray-100 text-gray-700';
        },
        money(value) {
            if (value === null || value === undefined) return '—';
            return '$' + Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        syncStripe() {
            this.syncing = true;
            axios.post(route('bridge.support.sync-stripe', this.supportUser.id))
                .then((response) => {
                    if (this.supportUser.role === 'Agent') {
                        this.subscriptionData = response.data;
                    } else {
                        this.paymentsData = response.data;
                    }
                })
                .catch(() => {
                    window.alert('Could not sync from Stripe. Please try again.');
                })
                .finally(() => {
                    this.syncing = false;
                });
        },
        applicationStatusBadge(status) {
            const s = String(status || '').toLowerCase();
            if (['approved', 'complete', 'completed', 'accepted', 'active'].includes(s)) return 'bg-green-100 text-green-700';
            if (['declined', 'rejected', 'denied', 'failed'].includes(s)) return 'bg-red-100 text-red-700';
            if (['pending', 'submitted', 'in progress', 'in_progress', 'incomplete', 'processing', 'review'].includes(s)) return 'bg-amber-100 text-amber-700';
            if (['withdrawn', 'archived', 'cancelled', 'canceled', 'expired'].includes(s)) return 'bg-gray-100 text-gray-600';
            return 'bg-sky-100 text-sky-700';
        },
        humanizeStatus(status) {
            if (!status) return '—';
            return String(status).replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        },
    },
};
</script>
